import type { App } from "vue";
import {
  useToast as useFinmonyToast,
  type ToastOptions as FinToastOptions,
  type ToastPosition,
  type ToastType,
} from "@finmony/ui";

type ToastContent =
  | string
  | number
  | Error
  | {
      message?: string;
      title?: string;
      props?: {
        message?: string;
        title?: string;
      };
    };

export interface PluginOptions {
  position?: ToastPosition;
  timeout?: number;
  maxToasts?: number;
  closeOnClick?: boolean;
  pauseOnFocusLoss?: boolean;
  pauseOnHover?: boolean;
  draggable?: boolean;
  draggablePercent?: number;
  showCloseButtonOnHover?: boolean;
  hideProgressBar?: boolean;
  closeButton?: boolean | "button" | "icon";
  icon?: boolean;
  rtl?: boolean;
  newestOnTop?: boolean;
}

export const POSITION = {
  TOP_LEFT: "top-left",
  TOP_RIGHT: "top-right",
  BOTTOM_LEFT: "bottom-left",
  BOTTOM_RIGHT: "bottom-right",
  TOP_CENTER: "top-center",
  BOTTOM_CENTER: "bottom-center",
} as const;
export type POSITION = typeof POSITION;

const defaults = {
  position: POSITION.TOP_RIGHT as ToastPosition,
  timeout: 5000,
  maxToasts: 5,
};

const core = useFinmonyToast();

const normalizePosition = (position?: ToastPosition | string) => {
  if (!position) return defaults.position;
  const normalized = position.toLowerCase() as ToastPosition;
  return (Object.values(POSITION) as string[]).includes(normalized) ? normalized : defaults.position;
};

const normalizeContent = (content: ToastContent) => {
  if (content instanceof Error) {
    return { message: content.message, title: undefined as string | undefined };
  }

  if (typeof content === "string" || typeof content === "number") {
    return { message: String(content), title: undefined as string | undefined };
  }

  const title = content.title ?? content.props?.title;
  const message = content.message ?? content.props?.message ?? "";

  return { message, title };
};

type NormalizedOptions = Partial<FinToastOptions> & { position?: ToastPosition; timeout?: number };

const normalizeOptions = (options?: NormalizedOptions) => {
  const position = normalizePosition(options?.position);
  const duration = options?.timeout ?? options?.duration ?? defaults.timeout;

  return {
    position,
    duration,
    dismissible: options?.dismissible ?? true,
    action: options?.action,
    title: options && "title" in options ? (options as FinToastOptions).title : undefined,
  };
};

const show = (type: ToastType, content: ToastContent, options?: NormalizedOptions) => {
  const { message, title } = normalizeContent(content);
  const normalized = normalizeOptions(options);

  core.setPosition(normalized.position);

  return core[type](message, {
    ...normalized,
    title,
  });
};

const createToastApi = () => {
  const toast = ((content: ToastContent, options?: NormalizedOptions) => show("info", content, options)) as any;

  toast.success = (content: ToastContent, options?: NormalizedOptions) => show("success", content, options);
  toast.error = (content: ToastContent, options?: NormalizedOptions) => show("error", content, options);
  toast.info = (content: ToastContent, options?: NormalizedOptions) => show("info", content, options);
  toast.warning = (content: ToastContent, options?: NormalizedOptions) => show("warning", content, options);
  toast.clear = () => core.clearAll();
  toast.dismiss = (id: string) => core.removeToast(id);

  return toast;
};

const ToastPlugin = {
  install(app: App, options?: PluginOptions) {
    defaults.position = normalizePosition(options?.position);
    defaults.timeout = options?.timeout ?? defaults.timeout;

    if (options?.maxToasts) {
      defaults.maxToasts = options.maxToasts;
      core.setMaxToasts(options.maxToasts);
    }

    core.setPosition(defaults.position);

    const api = createToastApi();
    app.config.globalProperties.$toast = api;
    app.provide("toast", api);
  },
};

export function useToast() {
  return createToastApi();
}

export type { ToastType, ToastPosition } from "@finmony/ui/composables/useToast";
export default ToastPlugin;
